import { z } from 'zod'

/** An API failure with the stable code from the server (ADR-0007 clause 9), or `network`. */
export class ApiError extends Error {
  readonly status: number
  readonly code: string
  constructor(status: number, code: string) {
    super(code)
    this.status = status
    this.code = code
  }
}

const errorBody = z.object({ code: z.string() })

/**
 * The access token lives in memory only; the refresh token is an HttpOnly cookie the page can't
 * read (ADR-0010 clause 9). Same-origin `/v1`, so no CORS (ADR-0011 clause 3).
 */
let accessToken: string | null = null
let refreshing: Promise<boolean> | null = null
let onSessionLost: () => void = () => {}

export function setAccessToken(token: string | null): void {
  accessToken = token
}

export function onSessionExpired(handler: () => void): void {
  onSessionLost = handler
}

const tokenOut = z.object({
  access_token: z.string(),
  expires_in: z.number().int(),
  password_must_change: z.boolean(),
})
export type TokenOut = z.infer<typeof tokenOut>

async function raw(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, {
      ...init,
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-Requested-With': 'gammapos', // CSRF guard on cookie endpoints (ADR-0010 clause 9)
        ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
        ...init.headers,
      },
    })
  } catch {
    throw new ApiError(0, 'network')
  }
}

async function fail(res: Response): Promise<never> {
  const body: unknown = await res.json().catch(() => ({}))
  const parsed = errorBody.safeParse(body)
  throw new ApiError(res.status, parsed.success ? parsed.data.code : `http_${res.status}`)
}

/** Exchanges the refresh cookie for a new access token. Single-flight: concurrent 401s share one. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= (async () => {
    try {
      const res = await raw('/v1/auth/refresh', { method: 'POST' })
      if (!res.ok) return false
      setAccessToken(tokenOut.parse(await res.json()).access_token)
      return true
    } finally {
      refreshing = null
    }
  })()
  return refreshing
}

export async function api<T>(path: string, schema: z.ZodType<T>, init: RequestInit = {}): Promise<T> {
  let res = await raw(path, init)
  if (res.status === 401 && accessToken) {
    // Expired access token: refresh once and retry. If that fails, the session is over.
    if (await refreshSession()) {
      res = await raw(path, init)
    } else {
      setAccessToken(null)
      onSessionLost()
    }
  }
  if (!res.ok) return fail(res)
  if (res.status === 204) return schema.parse(undefined)
  return schema.parse(await res.json())
}

export async function authRequest(path: string, body: unknown): Promise<TokenOut> {
  const res = await raw(path, { method: 'POST', body: JSON.stringify(body) })
  if (!res.ok) return fail(res)
  const out = tokenOut.parse(await res.json())
  setAccessToken(out.access_token)
  return out
}

/** Downloads an authenticated file (CSV, zip): a plain link can't carry the bearer token. */
export async function download(path: string, filename: string): Promise<void> {
  let res = await raw(path, { method: 'GET' })
  if (res.status === 401 && (await refreshSession())) res = await raw(path, { method: 'GET' })
  if (!res.ok) return fail(res)
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.append(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
